import { normalizeEuro } from '@amo/core/utils/normalize';
import { getTranslation } from '@amo/core/utils/index.js';
import { getIconOptions, getTextOptions } from '../Options/getTextOptions.js';
import { getIconByOptions } from '../Options/getIconByOptions.jsx';
import Incendie from '@amo/core/assets/images/icons/Incendie.svg';
import Warning from '@amo/core/assets/images/icons/warning.svg';
import _ from 'lodash';

const franchiseIcons = {
    RAFRAVI: Incendie,
    RAFRADTA: Warning,
};

// Strips HTML tags and decodes entities from trusted i18n strings to get plain text
const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
};

// Splits text into [description, markerLabel, restOfHighlight] at "Les + " marker
const splitAtLesPlus = (html) => {
    if (!html) return ['', '', ''];
    const plain = stripHtml(html);
    const markerRegex = /Les \+\s*(APRIL Moto\s*:\s*)?/;
    const match = plain.match(markerRegex);
    if (!match) return [plain, '', ''];
    const markerEnd = match.index + match[0].length;
    return [
        plain.substring(0, match.index).trim(),
        match[0].trim(),
        plain.substring(markerEnd).trim(),
    ];
};

const ToggleBlock = ({ option, data, changeValue, fractionnementChoisi, codePromo, apporteur1, fieldPrefix = 'options', levels = null }) => {
    const effectiveLevels = levels || option.data;
    const selected = _.get(data, `${fieldPrefix}[${option.idGroupe}]`);
    const isEnabled = !!selected;

    const handleToggle = () => {
        if (isEnabled) {
            changeValue(`${fieldPrefix}[${option.idGroupe}]`, undefined);
        } else if (effectiveLevels.length > 0) {
            changeValue(`${fieldPrefix}[${option.idGroupe}]`, effectiveLevels[0].value);
        }
    };

    const handleSelectLevel = (value) => {
        changeValue(`${fieldPrefix}[${option.idGroupe}]`, value);
    };

    const textRaw = getTextOptions(option.idGroupe, apporteur1?.codeBelair);
    const [descText, markerText, highlightRest] = splitAtLesPlus(textRaw);

    const toggleId = `toggle-${option.idGroupe}`;

    return (
        <div className="perso-block">
            <div className="toggle-row">
                <div className="toggle-info">
                    <div className="toggle-title" id={`${toggleId}-label`}>{option.labelGroupe}</div>
                    {descText && (
                        <div className="toggle-desc">{descText}</div>
                    )}
                </div>
                <label className="toggle-sw" htmlFor={toggleId}>
                    <input
                        id={toggleId}
                        type="checkbox"
                        checked={isEnabled}
                        onChange={handleToggle}
                        aria-labelledby={`${toggleId}-label`}
                    />
                    <span className="toggle-slider" />
                    <span className="visually-hidden">Activer {option.labelGroupe}</span>
                </label>
            </div>
            {effectiveLevels.length > 1 && (
                <div
                    className={`radio-options ${!isEnabled ? 'radio-options--disabled' : ''}`}
                    role="radiogroup"
                    aria-label={`Choix ${option.labelGroupe}`}
                >
                    {effectiveLevels.map((d, idx) => {
                        const isActive = selected === d.value;
                        const priceNormal = d.dataAffichage.prix;
                        const pricePromo = d.dataAffichage.prix_promo;
                        const isPromo = codePromo?.Eligible && pricePromo && pricePromo < priceNormal;
                        const displayPrice = isPromo ? pricePromo : priceNormal;
                        const period = fractionnementChoisi === 'M' ? getTranslation('months') : getTranslation('year');
                        return (
                            <div
                                key={idx}
                                className={`radio-opt ${isActive ? 'active' : ''}`}
                                onClick={() => handleSelectLevel(d.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSelectLevel(d.value)}
                                role="radio"
                                aria-checked={isActive}
                                aria-label={`${d.label} - ${normalizeEuro(displayPrice)} / ${period}`}
                                tabIndex={0}
                            >
                                <div className="radio-opt-left">
                                    <div className="radio-circle">
                                        <div className="radio-dot" />
                                    </div>
                                    <span className="radio-label">{d.label}</span>
                                </div>
                                <span className="radio-price">
                                    {normalizeEuro(displayPrice)} / {period}
                                    {isPromo && (
                                        <span className="radio-price-old"> {normalizeEuro(priceNormal)}</span>
                                    )}
                                </span>
                            </div>
                        );
                    })}
                </div>
            )}
            {(markerText || highlightRest) && (
                <div className="note-airbag">
                    <span><strong>{markerText}</strong> {highlightRest}</span>
                </div>
            )}
        </div>
    );
};

const optionDescriptions = {
    RAFRAVI: {
        desc: 'Votre moto mérite le meilleur ! Nous prenons en charge la franchise en cas de vol, incendie, explosion ou dommages liés aux forces de la nature.',
    },
    RAFRADTA: {
        desc: 'Une protection complète, sans compromis. Supprimez votre franchise en cas de dommages tous accidents.',
    },
    KSM: {
        desc: <>Ne bricolez plus pour économiser. Nous prenons en charge vos frais de réparation si vous tombez en panne. <strong>Les + APRIL Moto :</strong> Les frais de réparation sont pris en charge pendant les 10 premières années du véhicule.</>,
    },
    VALMAJ: {
        desc: <>Votre véhicule est volé ou irréparable, augmentez votre remboursement ! <strong>Les + APRIL Moto :</strong> Nous vous remboursons votre 2-roues : à sa valeur d'achat jusqu'à 24 mois selon la date de 1ère mise en circulation (aucune décote), à sa valeur à dire d'expert majorée de 15% au-delà des 24 mois (avec en plus un minimum garanti !).</>,
    },
};

const OptionCard = ({ option, data, changeValue, fractionnementChoisi, codePromo, franchiseAmount }) => {
    const idGroupe = option.idGroupe;
    const isFranchise = idGroupe === 'RAFRAVI' || idGroupe === 'RAFRADTA';

    const isActive = isFranchise
        ? _.get(data, `optionsFranchise[${idGroupe}]`) === 'true'
        : !!_.get(data, `options[${idGroupe}]`);

    const handleClick = () => {
        if (isFranchise) {
            const current = _.get(data, `optionsFranchise[${idGroupe}]`);
            changeValue(`optionsFranchise[${idGroupe}]`, current === 'true' ? 'false' : 'true');
        } else {
            if (isActive) {
                changeValue(`options[${idGroupe}]`, undefined);
            } else if (option.data && option.data.length > 0) {
                changeValue(`options[${idGroupe}]`, option.data[0].value);
            }
        }
    };

    const price = option.data?.[0]?.dataAffichage?.prix;
    const pricePromo = option.data?.[0]?.dataAffichage?.prix_promo;
    const isPromo = codePromo?.Eligible && pricePromo && pricePromo < price;
    const displayPrice = isPromo ? pricePromo : price;
    const icon = franchiseIcons[idGroupe] || getIconOptions(idGroupe) || getIconByOptions(idGroupe);
    const title = option.labelGroupe || option.data?.[0]?.dataAffichage?.titre;
    const conditionsIndemnisation = option.data?.[0]?.dataAffichage?.conditionsIndemnisation;
    const optDesc = optionDescriptions[idGroupe];

    return (
        <div className="opt-card-op__wrapper">
            <div
                className={`opt-card-op ${isActive ? 'active' : ''}`}
                onClick={handleClick}
                role="checkbox"
                aria-checked={isActive}
                aria-label={title}
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && handleClick()}
            >
                <div className="opt-card-op__hd">
                    <div className="opt-card-op__title-row">
                        {icon && (
                            <div className="opt-card-op__icon">
                                <img src={icon} alt="" loading="lazy" aria-hidden="true" />
                            </div>
                        )}
                        <div className="opt-card-op__title">{title}</div>
                    </div>
                    <div className="opt-card-op__check">{'\u2713'}</div>
                </div>
                {optDesc && (
                    <div className="opt-card-op__desc">{optDesc.desc}</div>
                )}
                {isFranchise && !isActive && franchiseAmount && (
                    <div className="opt-card-op__desc">Franchise actuelle : {franchiseAmount} €</div>
                )}
                {conditionsIndemnisation && (
                    <div className="opt-card-op__desc">{conditionsIndemnisation}</div>
                )}
                {displayPrice != null && (
                    <div className="opt-card-op__price">
                        {normalizeEuro(displayPrice)} / {fractionnementChoisi === 'M' ? getTranslation('months') : getTranslation('year')}
                        {isPromo && (
                            <span className="opt-card-op__price-old"> {normalizeEuro(price)}</span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const PersonalizeSection = (props) => {
    const { options, franchises, data, changeValue, fractionnementChoisi, codePromo, apporteur1, formules, codeFormuleChoisie } = props;

    const pccOption = options?.find((o) => o.idGroupe === 'PCC');
    const assOption = options?.find((o) => o.idGroupe === 'ASS');
    const accesseqpOption = options?.find((o) => o.idGroupe === 'ACCESSEQP');
    const accesseqpLevels = accesseqpOption?.data?.[0]?.listeTarifOption;

    const additionalOptions = options?.filter((o) =>
        o.idGroupe !== 'PCC' && o.idGroupe !== 'ASS' && o.idGroupe !== 'ACCESSEQP' && o.labelGroupe
    ) || [];
    const franchiseOptions = franchises?.data?.map((f) => ({
        idGroupe: f.value,
        labelGroupe: f.label,
        data: [f],
    })) || [];

    // Montants de franchise par code garantie
    const getFranchiseAmount = (code) => {
        const codeGarantie = code === 'RAFRADTA' ? 'DTA' : 'VI';
        const formule = _.find(formules, ['CodeFormule', codeFormuleChoisie]);
        const garantie = _.find(formule?.ListeGaranties, ['CodeGarantie', codeGarantie]);
        const amounts = garantie?.ConditionsIndemnisationSansRachat?.match(/\d+/g);
        return amounts ? amounts.join(' ') : null;
    };

    const allExtraOptions = [...franchiseOptions, ...additionalOptions];

    return (
        <div style={{textAlign: 'left'}}>
            {pccOption && (
                <ToggleBlock
                    option={pccOption}
                    data={data}
                    changeValue={changeValue}
                    fractionnementChoisi={fractionnementChoisi}
                    codePromo={codePromo}
                    apporteur1={apporteur1}
                />
            )}
            {assOption && (
                <ToggleBlock
                    option={assOption}
                    data={data}
                    changeValue={changeValue}
                    fractionnementChoisi={fractionnementChoisi}
                    codePromo={codePromo}
                    apporteur1={apporteur1}
                />
            )}
            {accesseqpOption && accesseqpLevels?.length > 0 && (
                <ToggleBlock
                    option={accesseqpOption}
                    data={data}
                    changeValue={changeValue}
                    fractionnementChoisi={fractionnementChoisi}
                    codePromo={codePromo}
                    apporteur1={apporteur1}
                    fieldPrefix="optionsListe"
                    levels={accesseqpLevels}
                />
            )}
            {allExtraOptions.length > 0 && (
                <fieldset className="perso-block" style={{border: 'none', padding: 0, margin: 0}}>
                    <legend className="toggle-title" style={{ marginBottom: 16, marginTop: 22 }}>
                        Options supplémentaires
                    </legend>
                    <div className="opts-grid">
                        {allExtraOptions.map((opt) => (
                            <OptionCard
                                key={opt.idGroupe}
                                option={opt}
                                data={data}
                                changeValue={changeValue}
                                fractionnementChoisi={fractionnementChoisi}
                                codePromo={codePromo}
                                franchiseAmount={(opt.idGroupe === 'RAFRAVI' || opt.idGroupe === 'RAFRADTA') ? getFranchiseAmount(opt.idGroupe) : null}
                            />
                        ))}
                    </div>
                </fieldset>
            )}
        </div>
    );
};

export default PersonalizeSection;
